import clsx from "clsx";
import { FC, ReactNode } from "react";
import {
  AssetLabel,
  assetLabel,
  Decimal,
  Fiat,
  formatAprValue,
  IconDenom,
  useTranslation,
} from "rujira.ui";
import { formatBigintPercent } from "../../../common/bigint";
import { RangePip } from "../../../trade/components/Orders/shared/RangeBar";
import { OrderRow } from "../../../trade/components/mobile/OrderRow";
import { FinRangeStatus } from "../../analytics/__generated__/AlgorithmicLeaderboardQuery.graphql";
import {
  formatAge,
  formatMultiple,
  pct,
  truncateAddress,
} from "../../leaderboard/format";
import { Row } from "../../leaderboard/types";
import { RangeActions } from "../RangeActions";
import { RangeCell } from "../RangeCell";

const Stat: FC<{
  label: string;
  align?: "right";
  children: ReactNode;
}> = ({ label, align, children }) => (
  <div className={clsx("flex dir-c gap-0.5", { "ai-e": align === "right" })}>
    <span className="mobile-card__label">{label}</span>
    <span className="nowrap fs-14 mono color-white">{children}</span>
  </div>
);

export const LeaderboardRow: FC<{
  row: Row;
  status: FinRangeStatus;
}> = ({ row, status }) => {
  const { t } = useTranslation("common");
  const { node, baseSymbol, quoteSymbol, valueQuote } = row;
  const { assetBase, assetQuote } = node.pair;
  const a = node.analytics;
  const noYield = BigInt(node.fee) === 0n;
  const low = BigInt(node.low);
  const high = BigInt(node.high);
  const current = BigInt(node.price);

  const label = (asset: typeof assetBase) => (
    <AssetLabel
      asset={asset}
      Container={({ children }) => (
        <small className="color-grey fs-11">{children}</small>
      )}
    />
  );

  const apr = (
    <span className="algorithmic-leaderboard__apr mono color-teal">
      {noYield ? t("compounding") : formatAprValue(BigInt(a?.apr ?? 0n))}
    </span>
  );

  return (
    <OrderRow
      icons={
        <>
          <IconDenom denom={baseSymbol} />
          <IconDenom denom={quoteSymbol} />
        </>
      }
      title={
        <span className="algorithmic-leaderboard__pair">
          {label(assetBase)}
          <span className="color-grey">/</span>
          {label(assetQuote)}
        </span>
      }
      label={`${assetLabel(assetBase)}/${assetLabel(assetQuote)}`}
      aside={
        <RangePip
          current={current}
          low={low}
          high={high}
          inRange={current >= low && current <= high}
          closed={status === "CLOSED"}
        />
      }
      secondary={
        noYield ? (
          apr
        ) : (
          <>
            <span className="mobile-card__label mr-0.5">{t("apr")}</span>
            {apr}
          </>
        )
      }>
      <div className="algorithmic-leaderboard__expanded">
        <RangeCell node={node} status={status} />

        <div className="mobile-card__panel algorithmic-leaderboard__value">
          <Decimal
            amount={valueQuote}
            symbol={quoteSymbol}
            decimals={assetQuote.metadata.decimals}
            className="fs-16 condensed fw-500"
            clip
          />
          <Fiat
            amount={BigInt(node.valueUsd)}
            symbol="$"
            decimals={8}
            className="color-grey fs-14 fw-500 condensed"
          />
        </div>

        <div className="mobile-card__panel algorithmic-leaderboard__stats">
          <Stat label="Age">
            {a && row.ageValid ? formatAge(row.nums.age) : "—"}
          </Stat>
          <Stat label="MOIC">{formatMultiple(row.nums.moic)}</Stat>
          <Stat label="DPI" align="right">
            {formatMultiple(row.nums.dpi)}
          </Stat>
          <Stat label="Tightness">
            {row.nums.tightness === null ? "—" : pct(row.nums.tightness)}
          </Stat>
          <Stat label="Spread">{formatBigintPercent(BigInt(node.spread))}</Stat>
          <Stat label="Rewards" align="right">
            {noYield || row.nums.fee === null ? (
              <span className="color-grey fs-12">
                {t("compounding").toUpperCase()}
              </span>
            ) : (
              pct(row.nums.fee)
            )}
          </Stat>
        </div>

        <div className="algorithmic-leaderboard__footer">
          <span className="mono color-grey fs-12">
            {truncateAddress(node.owner)}
          </span>
          <RangeActions row={row} compact />
        </div>
      </div>
    </OrderRow>
  );
};
